import React, { useState } from 'react';
import { Form, Button, Row, Col, Card, Spinner, Alert, Modal, ListGroup, Tab, Tabs } from 'react-bootstrap';
import { FaWallet, FaUserPlus, FaLink, FaHistory, FaChartBar, FaUndo } from 'react-icons/fa';
import api from '../services/api';

const WalletMoneyModal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('agent');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Agent Registration Data
  const [agentData, setAgentData] = useState({
    agent_name: '',
    agent_mobile: '',
    agent_email: '',
    agent_address: '',
    firm_name: '',
    pincode: '',
    city: '',
    state: '',
    pan_number: '',
    aadhar_number: '',
    bank_account: '',
    ifsc_code: '',
    bank_name: ''
  });

  // URL Generation Data (as per PaySprint documentation)
  const [urlData, setUrlData] = useState({
    merchant_code: '',
    agent_code: '', // Alternative field name
    refid: '',
    reference_id: '', // Alternative field name
    redirect_url: ''
  });

  // Status Check Data
  const [statusData, setStatusData] = useState({
    refid: '',
    reference_id: '', // Alternative field name
    merchant_code: '',
    agent_code: '' // Alternative field name
  });

  // Refund Data
  const [refundData, setRefundData] = useState({
    reference_id: '',
    amount: '',
    reason: '',
    otp: ''
  });

  // History and Stats
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({});

  // Agent Registration
  const handleAgentRegistration = async () => {
    if (!agentData.agent_name || !agentData.agent_mobile) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/wallet-money/agent/registration', agentData);

      if (response.data.success) {
        setSuccess('Agent registered successfully!');
        setAgentData({
          agent_name: '',
          agent_mobile: '',
          agent_email: '',
          agent_address: '',
          firm_name: '',
          pincode: '',
          city: '',
          state: '',
          pan_number: '',
          aadhar_number: '',
          bank_account: '',
          ifsc_code: '',
          bank_name: ''
        });
      } else {
        setError(response.data.message || 'Agent registration failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Agent registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Generate URL
  const handleGenerateUrl = async () => {
    // Updated validation as per PaySprint documentation
    if (!urlData.merchant_code && !urlData.agent_code) {
      setError('Please enter merchant code');
      return;
    }
    if (!urlData.refid && !urlData.reference_id) {
      setError('Please enter reference ID');
      return;
    }
    if (!urlData.redirect_url) {
      setError('Please enter redirect URL');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Map to PaySprint API format
      const payload = {
        merchant_code: urlData.merchant_code || urlData.agent_code,
        refid: urlData.refid || urlData.reference_id,
        redirect_url: urlData.redirect_url
      };

      const response = await api.post('/api/wallet-money/generate-url', payload);

      if (response.data.success) {
        setSuccess('URL generated successfully!');
        
        // PaySprint requires POST form redirect with encdata
        const url = response.data.data?.url;
        const encdata = response.data.data?.encdata;
        
        if (url && encdata) {
          // Create and submit POST form as per PaySprint documentation
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = url;
          form.target = '_blank';
          
          const encdataField = document.createElement('textarea');
          encdataField.name = 'encdata';
          encdataField.style.display = 'none';
          encdataField.value = encdata;
          form.appendChild(encdataField);
          
          document.body.appendChild(form);
          form.submit();
          document.body.removeChild(form);
        } else if (url) {
          // Fallback: open URL directly if encdata not available
          window.open(url, '_blank');
        }
      } else {
        setError(response.data.message || 'URL generation failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'URL generation failed');
    } finally {
      setLoading(false);
    }
  };

  // Check Fund Transfer Status
  const handleFundTransferStatus = async () => {
    if (!statusData.reference_id) {
      setError('Please enter reference ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/wallet-money/fund-transfer/status', statusData);

      if (response.data.success) {
        setSuccess(`Fund Transfer Status: ${response.data.data?.status || 'Unknown'}`);
      } else {
        setError(response.data.message || 'Failed to fetch fund transfer status');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch fund transfer status');
    } finally {
      setLoading(false);
    }
  };

  // Check Wallet Load Status
  const handleWalletLoadStatus = async () => {
    if (!statusData.refid && !statusData.reference_id) {
      setError('Please enter reference ID');
      return;
    }
    if (!statusData.merchant_code && !statusData.agent_code) {
      setError('Please enter merchant code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Map to PaySprint API format
      const payload = {
        refid: statusData.refid || statusData.reference_id,
        merchant_code: statusData.merchant_code || statusData.agent_code
      };

      const response = await api.post('/api/wallet-money/wallet-load/status', payload);

      if (response.data.success) {
        const data = response.data.data?.data || response.data.data;
        const statusMsg = data?.txn_status 
          ? `Wallet Load Status: ${data.txn_status}`
          : `Wallet Load Status: ${response.data.message || 'Success'}`;
        setSuccess(statusMsg);
      } else {
        setError(response.data.message || 'Failed to fetch wallet load status');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch wallet load status');
    } finally {
      setLoading(false);
    }
  };

  // Refund Transaction
  const handleRefundTransaction = async () => {
    if (!refundData.reference_id || !refundData.amount) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/wallet-money/refund/transaction', {
        reference_id: refundData.reference_id,
        amount: refundData.amount,
        reason: refundData.reason
      });

      if (response.data.success) {
        setSuccess('Refund transaction initiated successfully! Please verify OTP to complete.');
        setActiveTab('refund');
      } else {
        setError(response.data.message || 'Refund transaction failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Refund transaction failed');
    } finally {
      setLoading(false);
    }
  };

  // Verify Refund OTP
  const handleRefundVerifyOtp = async () => {
    if (!refundData.reference_id || !refundData.otp) {
      setError('Please enter reference ID and OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/wallet-money/refund/verify-otp', {
        reference_id: refundData.reference_id,
        otp: refundData.otp
      });

      if (response.data.success) {
        setSuccess('Refund OTP verified successfully! Refund processed.');
        setRefundData({
          reference_id: '',
          amount: '',
          reason: '',
          otp: ''
        });
      } else {
        setError(response.data.message || 'Refund OTP verification failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Refund OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Load Transaction History
  const loadTransactionHistory = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get('/api/wallet-money/transactions/history');

      if (response.data.success) {
        setTransactions(response.data.data?.transactions || []);
        setSuccess('Transaction history loaded successfully!');
      } else {
        setError(response.data.message || 'Failed to load transaction history');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  // Load Wallet Statistics
  const loadWalletStats = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get('/api/wallet-money/statistics');

      if (response.data.success) {
        setStats(response.data.data || {});
        setSuccess('Wallet statistics loaded successfully!');
      } else {
        setError(response.data.message || 'Failed to load wallet statistics');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load wallet statistics');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setError('');
    setSuccess('');
    setAgentData({
      agent_name: '',
      agent_mobile: '',
      agent_email: '',
      agent_address: '',
      firm_name: '',
      pincode: '',
      city: '',
      state: '',
      pan_number: '',
      aadhar_number: '',
      bank_account: '',
      ifsc_code: '',
      bank_name: ''
    });
    setUrlData({
      mobile: '',
      amount: '',
      reference_id: '',
      callback_url: '',
      redirect_url: '',
      description: ''
    });
    setStatusData({
      reference_id: ''
    });
    setRefundData({
      reference_id: '',
      amount: '',
      reason: '',
      otp: ''
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaWallet className="me-2" />
          Wallet Money API
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          {/* Agent Registration Tab */}
          <Tab eventKey="agent" title="Agent Registration">
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <FaUserPlus className="me-2" />
                  Agent Registration
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Agent Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={agentData.agent_name}
                        onChange={(e) => setAgentData(prev => ({ ...prev, agent_name: e.target.value }))}
                        placeholder="Enter agent name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Number *</Form.Label>
                      <Form.Control
                        type="text"
                        value={agentData.agent_mobile}
                        onChange={(e) => setAgentData(prev => ({ ...prev, agent_mobile: e.target.value }))}
                        placeholder="Enter 10-digit mobile number"
                        maxLength={10}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={agentData.agent_email}
                        onChange={(e) => setAgentData(prev => ({ ...prev, agent_email: e.target.value }))}
                        placeholder="Enter email address"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Firm Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={agentData.firm_name}
                        onChange={(e) => setAgentData(prev => ({ ...prev, firm_name: e.target.value }))}
                        placeholder="Enter firm name"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={agentData.agent_address}
                    onChange={(e) => setAgentData(prev => ({ ...prev, agent_address: e.target.value }))}
                    placeholder="Enter address"
                  />
                </Form.Group>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Pincode</Form.Label>
                      <Form.Control
                        type="text"
                        value={agentData.pincode}
                        onChange={(e) => setAgentData(prev => ({ ...prev, pincode: e.target.value }))}
                        placeholder="Enter pincode"
                        maxLength={6}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        type="text"
                        value={agentData.city}
                        onChange={(e) => setAgentData(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="Enter city"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        type="text"
                        value={agentData.state}
                        onChange={(e) => setAgentData(prev => ({ ...prev, state: e.target.value }))}
                        placeholder="Enter state"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>PAN Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={agentData.pan_number}
                        onChange={(e) => setAgentData(prev => ({ ...prev, pan_number: e.target.value.toUpperCase() }))}
                        placeholder="Enter PAN number"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Aadhar Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={agentData.aadhar_number}
                        onChange={(e) => setAgentData(prev => ({ ...prev, aadhar_number: e.target.value }))}
                        placeholder="Enter Aadhar number"
                        maxLength={12}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bank Account</Form.Label>
                      <Form.Control
                        type="text"
                        value={agentData.bank_account}
                        onChange={(e) => setAgentData(prev => ({ ...prev, bank_account: e.target.value }))}
                        placeholder="Enter bank account number"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>IFSC Code</Form.Label>
                      <Form.Control
                        type="text"
                        value={agentData.ifsc_code}
                        onChange={(e) => setAgentData(prev => ({ ...prev, ifsc_code: e.target.value.toUpperCase() }))}
                        placeholder="Enter IFSC code"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Bank Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={agentData.bank_name}
                    onChange={(e) => setAgentData(prev => ({ ...prev, bank_name: e.target.value }))}
                    placeholder="Enter bank name"
                  />
                </Form.Group>

                <Button onClick={handleAgentRegistration} disabled={loading} className="w-100">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Register Agent
                </Button>
              </Card.Body>
            </Card>
          </Tab>

          {/* Generate URL Tab */}
          <Tab eventKey="url" title="Generate URL">
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <FaLink className="me-2" />
                  Generate Payment URL
                </h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Merchant Code *</Form.Label>
                  <Form.Control
                    type="text"
                    value={urlData.merchant_code || urlData.agent_code}
                    onChange={(e) => setUrlData(prev => ({ ...prev, merchant_code: e.target.value, agent_code: e.target.value }))}
                    placeholder="Enter merchant code (e.g., AGENT001)"
                  />
                  <Form.Text className="text-muted">
                    Merchant code of the partner merchant (successfully registered)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Reference ID *</Form.Label>
                  <Form.Control
                    type="text"
                    value={urlData.refid || urlData.reference_id}
                    onChange={(e) => setUrlData(prev => ({ ...prev, refid: e.target.value, reference_id: e.target.value }))}
                    placeholder="Enter unique reference ID"
                  />
                  <Form.Text className="text-muted">
                    Partner's System Unique reference number for each transaction
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Redirect URL *</Form.Label>
                  <Form.Control
                    type="url"
                    value={urlData.redirect_url}
                    onChange={(e) => setUrlData(prev => ({ ...prev, redirect_url: e.target.value }))}
                    placeholder="https://yourdomain.com/callback"
                  />
                  <Form.Text className="text-muted">
                    Redirect URL of partner server (customer will be redirected here after transaction)
                  </Form.Text>
                </Form.Group>

                <Button onClick={handleGenerateUrl} disabled={loading} className="w-100">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Generate Payment URL
                </Button>
              </Card.Body>
            </Card>
          </Tab>

          {/* Status Check Tab */}
          <Tab eventKey="status" title="Status Check">
            <Card>
              <Card.Header>
                <h5 className="mb-0">Transaction Status Check</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Reference ID *</Form.Label>
                  <Form.Control
                    type="text"
                    value={statusData.refid || statusData.reference_id}
                    onChange={(e) => setStatusData(prev => ({ ...prev, refid: e.target.value, reference_id: e.target.value }))}
                    placeholder="Enter reference ID (refid)"
                  />
                  <Form.Text className="text-muted">
                    Transaction Reference number that partner had passed in transaction api
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Merchant Code *</Form.Label>
                  <Form.Control
                    type="text"
                    value={statusData.merchant_code || statusData.agent_code}
                    onChange={(e) => setStatusData(prev => ({ ...prev, merchant_code: e.target.value, agent_code: e.target.value }))}
                    placeholder="Enter merchant code"
                  />
                  <Form.Text className="text-muted">
                    Required for Wallet Load Status Check
                  </Form.Text>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Button onClick={handleFundTransferStatus} disabled={loading} className="w-100">
                      {loading ? <Spinner size="sm" className="me-2" /> : null}
                      Check Fund Transfer Status
                    </Button>
                  </Col>
                  <Col md={6}>
                    <Button onClick={handleWalletLoadStatus} disabled={loading} variant="outline-primary" className="w-100">
                      {loading ? <Spinner size="sm" className="me-2" /> : null}
                      Check Wallet Load Status
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          {/* Refund Tab */}
          <Tab eventKey="refund" title="Refund">
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <FaUndo className="me-2" />
                  Refund Transaction
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Reference ID *</Form.Label>
                      <Form.Control
                        type="text"
                        value={refundData.reference_id}
                        onChange={(e) => setRefundData(prev => ({ ...prev, reference_id: e.target.value }))}
                        placeholder="Enter reference ID"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Amount *</Form.Label>
                      <Form.Control
                        type="number"
                        value={refundData.amount}
                        onChange={(e) => setRefundData(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="Enter refund amount"
                        min="1"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Reason</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={refundData.reason}
                    onChange={(e) => setRefundData(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Enter refund reason"
                  />
                </Form.Group>

                <Button onClick={handleRefundTransaction} disabled={loading} className="w-100 mb-3">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Initiate Refund
                </Button>

                <hr />

                <h6>Verify Refund OTP</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Reference ID *</Form.Label>
                      <Form.Control
                        type="text"
                        value={refundData.reference_id}
                        onChange={(e) => setRefundData(prev => ({ ...prev, reference_id: e.target.value }))}
                        placeholder="Enter reference ID"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>OTP *</Form.Label>
                      <Form.Control
                        type="text"
                        value={refundData.otp}
                        onChange={(e) => setRefundData(prev => ({ ...prev, otp: e.target.value }))}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Button onClick={handleRefundVerifyOtp} disabled={loading} variant="success" className="w-100">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Verify OTP & Complete Refund
                </Button>
              </Card.Body>
            </Card>
          </Tab>

          {/* History Tab */}
          <Tab eventKey="history" title="History">
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <FaHistory className="me-2" />
                  Transaction History
                </h5>
              </Card.Header>
              <Card.Body>
                <Button onClick={loadTransactionHistory} disabled={loading} className="w-100 mb-3">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Load Transaction History
                </Button>

                {transactions.length > 0 ? (
                  <ListGroup>
                    {transactions.map((transaction, index) => (
                      <ListGroup.Item key={index}>
                        <div className="d-flex justify-content-between">
                          <div>
                            <strong>Reference: {transaction.reference_id}</strong><br />
                            <small>Amount: ₹{transaction.amount}</small><br />
                            <small>Status: {transaction.status}</small>
                          </div>
                          <div className="text-muted">
                            <small>{new Date(transaction.created_at).toLocaleString()}</small>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <p className="text-muted text-center">No transactions found</p>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Statistics Tab */}
          <Tab eventKey="stats" title="Statistics">
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <FaChartBar className="me-2" />
                  Wallet Statistics
                </h5>
              </Card.Header>
              <Card.Body>
                <Button onClick={loadWalletStats} disabled={loading} className="w-100 mb-3">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Load Wallet Statistics
                </Button>

                {Object.keys(stats).length > 0 ? (
                  <Row>
                    <Col md={6}>
                      <Card className="mb-3">
                        <Card.Body>
                          <h6>Fund Transfers</h6>
                          <p><strong>Total:</strong> {stats.total_fund_transfers}</p>
                          <p><strong>Successful:</strong> {stats.successful_transfers}</p>
                          <p><strong>Failed:</strong> {stats.failed_transfers}</p>
                          <p><strong>Total Amount:</strong> ₹{stats.total_amount_transferred || 0}</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="mb-3">
                        <Card.Body>
                          <h6>Refunds</h6>
                          <p><strong>Total Refunds:</strong> {stats.total_refunds}</p>
                          <p><strong>Total Refund Amount:</strong> ₹{stats.total_refund_amount || 0}</p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                ) : (
                  <p className="text-muted text-center">No statistics available</p>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="outline-warning" onClick={handleReset}>
          Reset
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WalletMoneyModal;
